import { forwardRef, useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';

import usePermission from 'hooks/permission/usePermission';

import PermissionWrapper from 'components/PermissionWrapper';

import { ListItemButton, ListItemIcon, ListItemText, Typography, useTheme } from '@mui/material';

import { MenuContext } from 'layout/MainLayout';
import type { MenuItem } from 'menu-items/types';

// ==============================|| NAVIGATION ITEM ||============================== //

interface NavItemProps {
    item: MenuItem;
    level: number;
}

interface LinkProps {
    component: React.ElementType;
    href?: string;
    target?: string;
    to?: string;
}

const NavItem = ({ item, level }: NavItemProps) => {
    const theme = useTheme();
    const { isDrawerOpen, openItem, setOpenItem } = useContext(MenuContext);
    const { isAvailableProject } = usePermission();

    let itemTarget = '_self';
    if (item.target) {
        itemTarget = '_blank';
    }

    let listItemProps: LinkProps = {
        // eslint-disable-next-line react/display-name
        component: forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => (
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            <Link ref={ref} {...props} to={item.url ?? '/'} target={itemTarget} />
        )),
    };
    if (item.external) {
        listItemProps = { component: 'a', href: item.url, target: itemTarget };
    }

    const itemHandler = (id: string) => {
        setOpenItem(id);
    };

    const Icon = item.icon as React.ElementType;
    const itemIcon = item.icon ? <Icon style={{ fontSize: isDrawerOpen ? '1rem' : '1.25rem' }} /> : false;

    const isSelected = openItem === item.id;

    useEffect(() => {
        const currentIndex = document.location.pathname
            .toString()
            .split('/')
            .findIndex((id) => id === item.id);
        if (currentIndex > -1) {
            setOpenItem(item.id);
        }
    }, []);

    const textColor = 'text.primary';
    const iconSelectedColor = 'primary.main';

    function toCamelCase(sentence = '') {
        return sentence
            .toLowerCase()
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join('');
    }

    const isHiddenByProject = item.requiredProject && !isAvailableProject(item.requiredProject);
    const isHiddenByVisibility = 'isVisible' in item && !item.isVisible?.();

    if (isHiddenByProject || isHiddenByVisibility) {
        return null;
    }

    return (
        <PermissionWrapper type="get" name={item.permission_name}>
            <ListItemButton
                {...listItemProps}
                disabled={item.disabled}
                onClick={() => {
                    itemHandler(item.id);
                }}
                selected={isSelected}
                data-locator={`sidebar${toCamelCase(item.title)}`}
                sx={{
                    zIndex: 1201,
                    pl: isDrawerOpen ? `${level * 28}px` : 1.5,
                    py: !isDrawerOpen && level === 1 ? 1.25 : 1,
                    ...(isDrawerOpen && {
                        '&:hover': {
                            bgcolor: 'primary.lighter',
                        },
                        '&.Mui-selected': {
                            bgcolor: 'primary.lighter',
                            borderRight: `2px solid ${theme.palette.primary.main}`,
                            color: iconSelectedColor,
                            '&:hover': {
                                color: iconSelectedColor,
                                bgcolor: 'primary.lighter',
                            },
                        },
                    }),
                    ...(!isDrawerOpen && {
                        '&:hover': {
                            bgcolor: 'transparent',
                        },
                        '&.Mui-selected': {
                            '&:hover': {
                                bgcolor: 'transparent',
                            },
                            bgcolor: 'transparent',
                        },
                    }),
                }}
            >
                {itemIcon && (
                    <ListItemIcon
                        sx={{
                            minWidth: 28,
                            color: isSelected ? iconSelectedColor : textColor,
                            ...(!isDrawerOpen && {
                                borderRadius: 1.5,
                                width: 36,
                                height: 36,
                                alignItems: 'center',
                                justifyContent: 'center',
                                '&:hover': {
                                    bgcolor: 'secondary.lighter',
                                },
                            }),
                            ...(!isDrawerOpen &&
                                isSelected && {
                                    bgcolor: 'primary.lighter',
                                    '&:hover': {
                                        bgcolor: 'primary.lighter',
                                    },
                                }),
                        }}
                    >
                        {itemIcon}
                    </ListItemIcon>
                )}
                {(isDrawerOpen || level !== 1) && (
                    <ListItemText
                        primary={
                            <Typography variant="body2" sx={{ color: isSelected ? iconSelectedColor : textColor }}>
                                {item.title}
                            </Typography>
                        }
                    />
                )}
            </ListItemButton>
        </PermissionWrapper>
    );
};

export default NavItem;
